import { enablePush, scheduleReminders } from "@/lib/push";
import { logWarn } from "@/lib/log";

// Hydration reminders: "time to drink" every 30 min while it is very hot and the pilgrim is outdoors.
// Settings live in localStorage; push items are queued server-side under kind "hydration".
export const HYDRATION_KEY = "hydration_reminder";
export const EVERY_MIN = 30;
const MAX_HOURS = 6;
export const AUTO_TIERS = new Set(["veryHot", "extreme"]);

export const readHydration = () => {
  try { return { auto: true, outdoor: false, until: null, startedAt: null, by: null, stoppedDay: null, pushOk: null, ...(JSON.parse(localStorage.getItem(HYDRATION_KEY)) || {}) }; }
  catch { return { auto: true, outdoor: false, until: null, startedAt: null, by: null, stoppedDay: null, pushOk: null }; }
};
export const writeHydration = (s) => { localStorage.setItem(HYDRATION_KEY, JSON.stringify(s)); return s; };
export const isActive = (s, now = Date.now()) => !!s.until && s.until > now;
export const dayKey = (d = new Date()) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;

// End of the session: today's Maghrib if ahead, capped at MAX_HOURS from now (fallback 2 h after dark).
export function sessionEnd(maghribAt, now = new Date()) {
  const cap = now.getTime() + MAX_HOURS * 3600000;
  if (maghribAt && maghribAt > now) return Math.min(maghribAt.getTime(), cap);
  return now.getTime() + 2 * 3600000;
}

export const nextDrinkAt = (s, now = Date.now()) => {
  if (!isActive(s, now) || !s.startedAt) return null;
  const step = EVERY_MIN * 60000;
  const k = Math.floor((now - s.startedAt) / step) + 1;
  const at = s.startedAt + k * step;
  return at <= s.until ? at : null;
};

// Drink slot index (1, 2, …) that is due now, or 0 before the first one.
export const dueSlot = (s, now = Date.now()) => (isActive(s, now) && s.startedAt ? Math.floor((now - s.startedAt) / (EVERY_MIN * 60000)) : 0);
export const drinksToday = (s) => (s.drinkDay === dayKey() ? s.drinks || 0 : 0);
// "I drank" → count a glass for today and mark the current slot as handled.
export const recordDrink = (slot) => writeHydration({ ...readHydration(), drinks: drinksToday(readHydration()) + 1, drinkDay: dayKey(), firedSlot: slot, snoozeAt: null });
export const snoozeDrink = (slot, minutes = 10) => writeHydration({ ...readHydration(), firedSlot: slot, snoozeAt: Date.now() + minutes * 60000 });

export function buildHydrationReminders(startedAt, until, texts) {
  const items = [];
  for (let at = startedAt + EVERY_MIN * 60000; at <= until; at += EVERY_MIN * 60000) {
    items.push({ send_at: new Date(at).toISOString(), title: texts.title, body: texts.body, url: "/" });
  }
  return items;
}

// Start a session (push best-effort; in-app timer always works). Returns the new settings.
export async function startHydration({ maghribAt, by = "manual", texts }) {
  const now = new Date();
  const s = { ...readHydration(), startedAt: now.getTime(), until: sessionEnd(maghribAt, now), by, stoppedDay: null, firedSlot: 0, snoozeAt: null };
  writeHydration(s);
  try {
    await enablePush();
    await scheduleReminders(buildHydrationReminders(s.startedAt, s.until, texts), "hydration");
    s.pushOk = true;
  } catch (e) {
    logWarn("hydration push unavailable:", e);
    s.pushOk = false;
  }
  return writeHydration(s);
}

export async function stopHydration({ manual = true } = {}) {
  const s = { ...readHydration(), until: null, startedAt: null, by: null, firedSlot: 0, snoozeAt: null, stoppedDay: manual ? dayKey() : readHydration().stoppedDay };
  writeHydration(s);
  try { await scheduleReminders([], "hydration"); } catch (e) { logWarn("hydration push clear failed:", e); }
  return s;
}
